// jQuery and its slider plugin are loaded by interactiveTaxSavingSlider.twig.
interface SliderOptions {
    range: string;
    min: number;
    max: number;
    step: number;
    value: number;
    slide: (event: Event, ui: { value: number }) => void;
}

declare const $: (selector: string) => {
    slider(options: SliderOptions): void;
};

function setHtml(id: string, value: string): void {
    const element = document.getElementById(id);
    if (element) element.innerHTML = value;
}

function deleteNonedigits(number: string) {
    if (isNaN(Number(number))) {
        return 0;
    } else {
        return number.replace(/\D/g, '');
    }
}

function calcBasicAllowance(income: number) {
    return Math.min(4_950, 0.15 * income);
}

function calcInvestmentAllowance(income: number) {
    let tax;
    if (income <= 33_000) {
        tax = 0;
    } else if (income <= 178_000) {
        tax = (income - 33_000) * 0.13;
    } else if (income <= 353_000) {
        tax = (178_000 - 33_000) * 0.13 + (income - 178_000) * 0.07;
    } else if (income <= 583_000) {
        tax =
            (178_000 - 33_000) * 0.13 +
            (353_000 - 178_000) * 0.07 +
            (income - 353_000) * 0.045;
    } else {
        tax = 41_450;
    }
    return tax;
}

function calculateTax(income: number) {
    let tax;
    if (income <= 12816) {
        tax = 0;
    } else if (income <= 20818) {
        tax = (income - 12816) * 0.2;
    } else if (income <= 34513) {
        tax = (20818 - 12816) * 0.2 + (income - 20818) * 0.3;
    } else if (income <= 66612) {
        tax =
            (20818 - 12816) * 0.2 +
            (34513 - 20818) * 0.3 +
            (income - 34513) * 0.4;
    } else if (income <= 99266) {
        tax =
            (20818 - 12816) * 0.2 +
            (34513 - 20818) * 0.3 +
            (66612 - 34513) * 0.4 +
            (income - 66612) * 0.48;
    } else if (income <= 1000000) {
        tax =
            (20818 - 12816) * 0.2 +
            (34513 - 20818) * 0.3 +
            (66612 - 34513) * 0.4 +
            (99266 - 66612) * 0.48 +
            (income - 99266) * 0.5;
    } else {
        tax =
            (20818 - 12816) * 0.2 +
            (34513 - 20818) * 0.3 +
            (66612 - 34513) * 0.4 +
            (99266 - 66612) * 0.48 +
            (1000000 - 99266) * 0.5 +
            (income - 1000000) * 0.55;
    }

    return tax / income;
}

export function calcInteractiveTaxSavings(profit: number) {
    // Grundfreibetrag
    const basicAllowance = calcBasicAllowance(profit);

    // Investitionsbedingter Gewinnfreibetrag
    const investmentAllowance = calcInvestmentAllowance(profit);

    // Veranlagebarer Gewinn
    const depositAmount = basicAllowance + investmentAllowance;

    /**
     *  Schiene 1 (Unternehmensgewinn abzüglich Grundfreibetrag und investitionsbedingter. Gewinnfreibetrag):
     **/

    // Unternehmensgewinn (abzgl Veranlagebarer Gewinn)
    const profitWithoutDepositAmount = +profit - depositAmount;

    // Durchschnittlicher Einkommenssteuersatz
    const averageTaxRateWithoutDepositAmount = calculateTax(
        profitWithoutDepositAmount
    );

    // Steuerbelastung (abzgl Veranlagebarer Gewinn)
    const taxWithoutDepositAmount =
        averageTaxRateWithoutDepositAmount * profitWithoutDepositAmount;

    /**
     * Schiene 2 (Unternehmensgewinn abzüglich Grundfreibetrag):
     * */

    // Unternehmensgewinn (abzgl Grundfreibetrag)
    const profitWithoutBasicAllowance = +profit - basicAllowance;

    // Durchschnittlicher Einkommenssteuersatz
    const averageTaxRateWithoutBasicAllowance = calculateTax(
        profitWithoutBasicAllowance
    );

    const taxWithoutBasicAllowance =
        averageTaxRateWithoutBasicAllowance * profitWithoutBasicAllowance;

    const taxSavings = taxWithoutBasicAllowance - taxWithoutDepositAmount;

    return {
        basicAllowance,
        investmentAllowance,
        depositAmount,
        profitWithoutDepositAmount,
        averageTaxRate: averageTaxRateWithoutDepositAmount,
        taxSavings
    };
}

function initSlider(): void {
    function update() {
        // Unternehmensgewinn
        const profit = Number(
            deleteNonedigits(document.getElementById('profit')?.innerHTML ?? '')
        );
        const {
            basicAllowance,
            investmentAllowance,
            averageTaxRate,
            taxSavings
        } = calcInteractiveTaxSavings(profit);

        // Grundfreibetrag
        setHtml(
            'basic-allowance',
            basicAllowance.toLocaleString('de-DE', { maximumFractionDigits: 0 })
        );

        // Investitionsbedingter Gewinnfreibetrag
        setHtml(
            'investment-allowance',
            investmentAllowance.toLocaleString('de-DE', {
                maximumFractionDigits: 0
            })
        );

        // Veranlagebarer Gewinn
        setHtml(
            'calced-profit',
            investmentAllowance.toLocaleString('de-DE', {
                maximumFractionDigits: 2
            })
        );

        // Unternehmensgewinn (abzgl Grundfreibetrag)

        // Durchschnittlicher Einkommenssteuersatz
        setHtml(
            'average-tax-rate',
            averageTaxRate.toLocaleString('de-DE', {
                maximumFractionDigits: 2,
                style: 'percent'
            })
        );

        // Steuerbelastung (abzgl Grundfreibetrag)
        setHtml(
            'calced-tax-savings',
            taxSavings.toLocaleString('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
        );
    }

    $('#profit-slider').slider({
        range: 'min',
        min: 33000,
        max: 583000,
        step: 500,
        value: 100000,
        slide: function (_event, ui) {
            setHtml(
                'profit',
                ui.value.toLocaleString('de-DE', {
                    maximumFractionDigits: 0
                })
            );
            update();
        }
    });

    update();
}

export default {
    init(): void {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initSlider, {
                once: true
            });
        } else {
            initSlider();
        }
    }
};
